"use client";

import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Bell, CheckCircle2, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";

/**
 * Reusable "notify me" waitlist form (WT6). Drop in anywhere an item isn't
 * buyable yet — the Preserved coming-soon landing, or a future out-of-stock
 * product. Posts to /api/waitlist with the given `interest` (a category slug
 * or product id).
 */
export function NotifyMe({
  interest,
  label = "Get notified when it launches",
  className = "",
}: {
  interest: string;
  label?: string;
  className?: string;
}) {
  const [mounted, setMounted] = useState(false);
  const [email, setEmail] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  useEffect(() => {
    // eslint-disable-next-line react-hooks/set-state-in-effect -- keep the SSR form inert until React owns its controlled input and submit handler.
    setMounted(true);
  }, []);

  const submit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (submitting) return;
    const trimmed = email.trim();
    if (!trimmed) {
      toast.error("Please enter your email.");
      return;
    }
    setSubmitting(true);
    try {
      const res = await fetch("/api/waitlist", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email: trimmed, interest }),
      });
      const json = (await res.json().catch(() => ({}))) as { error?: string };
      if (!res.ok) {
        toast.error(json.error ?? "Couldn't add you right now.");
        return;
      }
      setDone(true);
      toast.success("You're on the list!");
    } catch {
      toast.error("Network error. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  if (done) {
    return (
      <div
        // `data-slot` is the styling hook the dark-hero rules in globals.css
        // target (`.hero-cta-content [data-slot="notify-success"]`), matching
        // how <Input>/<Button> are restyled there. Without it this confirmation
        // renders dark-on-transparent and is invisible inside a hero.
        data-slot="notify-success"
        className={`flex items-center justify-center gap-2 rounded-lg border border-primary/30 bg-primary/5 px-4 py-3 text-sm text-foreground ${className}`}
        role="status"
        aria-live="polite"
      >
        <CheckCircle2 className="h-5 w-5 shrink-0 text-primary" aria-hidden="true" />
        We&apos;ll email you the moment it&apos;s live. Thank you!
      </div>
    );
  }

  return (
    <form onSubmit={submit} className={`flex flex-col gap-2 sm:flex-row ${className}`} aria-label="Notify me when available">
      <label htmlFor={`notify-${interest}`} className="sr-only">Email address</label>
      <Input
        id={`notify-${interest}`}
        type="email"
        inputMode="email"
        autoComplete="email"
        required
        disabled={!mounted || submitting}
        value={email}
        onChange={(e) => setEmail(e.target.value)}
        placeholder="you@example.com"
        maxLength={254}
        className="min-h-11 flex-1"
      />
      <Button type="submit" size="lg" disabled={!mounted || submitting} className="min-h-11 shrink-0">
        {submitting ? (
          <>
            <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" /> Adding…
          </>
        ) : (
          <>
            <Bell className="h-4 w-4" aria-hidden="true" /> {label}
          </>
        )}
      </Button>
    </form>
  );
}
